import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Link, Select, MenuItem, useSnackbar, TextField, Tooltip, Stack, Box, Spacer, ButtonContainer, Typography } from '@emburse/embark-core';
import { SupportIcon } from '@emburse/embark-icons';
import { useDialog } from '../../components/Dialog';
import { CopyableTextField, IconPopover } from '../../components';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { regenerateReceiptAuthCode, saveCompanyPreference, deleteCompanyPreference } from '../../api/api';
import { formatDate } from '../../util/dateAdapter';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
const validEmailListRegex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/;
function CorpSecuritySettings({ MinimumPasswordRating, FailedLoginNotification, MFACompanyOptOut, MandatoryMFA, IsAdmin, CompanyReceiptAuthCode }) {
    useTranslation();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    // Pageload and Default states
    const [defaultState, setDefaultState] = useState({
        minPasswordRating: MinimumPasswordRating,
        failedLogin: FailedLoginNotification,
        mfaCompanyOptOut: MFACompanyOptOut
    });
    const [receiptAuthCode, setReceiptAuthCode] = useState(CompanyReceiptAuthCode);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [minPasswordRating, setMinPasswordRating] = useState(defaultState.minPasswordRating);
    const [failedLogin, setFailedLogin] = useState({
        value: defaultState.failedLogin,
        isValid: true,
        helperText: ''
    });
    const [mfaCompanyOptOut, setMFACompanyOptOut] = useState(defaultState.mfaCompanyOptOut);
    const { enqueueSnackbar } = useSnackbar();
    const { enqueueDialog } = useDialog();
    const mfaInfo = $t(`When enabled, individual users may not opt out of Multi-Factor Authentication. If your company 
    chooses to opt out of MFA, users must individually maintain MFA opt out status through their MyAccount page.`);
    const passwordInfo = $t(`To keep your company’s account as secure as possible, the Minimum Password Rating will no longer 
    be configurable and all new passwords must meet our highest level of complexity. New passwords must be at least 8 characters
    and contain a lowercase, uppercase, numeric, and special character.`);
    const failedLoginInfo = $t(`If you would like to be notified when an employee fails to log into the application, 
    enter one or several email addresses which should be notified when this occurs. 
    One notification will be sent per day, even if there are no failed login attempts.`);
    const receiptExternalAccessCodeInfo = $t(`The receipt external access code allows external parties to access receipt images without logging into the application. 
    A company may only have one receipt external access code at a time. You may invalidate your current code by regenerating it below. 
    Codes expire one year after creation. After the code expires, a new one will be generated automatically.`);
    const doGenerateReceiptAuthCode = async () => {
        applyLoadingState();
        const result = await regenerateReceiptAuthCode();
        if (result.Status === 'success') {
            const data = JSON.parse(result.Data);
            setReceiptAuthCode(data);
            enqueueSnackbar($t('Receipt External Access Code updated'), {
                variant: 'alert',
                AlertProps: { severity: 'success' }
            });
        }
        else {
            enqueueSnackbar($t('Failed to update Receipt External Access Code. Please try again.'), { variant: 'alert', AlertProps: { severity: 'error' } });
        }
        removeLoadingState();
    };
    const handleRegenerateReceiptAuthCode = () => {
        if (!receiptAuthCode.Code) {
            doGenerateReceiptAuthCode();
            return;
        }
        enqueueDialog({
            title: $t('Confirmation'),
            show: true,
            content: (_jsxs(Stack, { spacing: 2, alignItems: "flex-start", children: [_jsx(Typography, { variant: "body1", children: $t(`Regenerating your company's receipt external access code will invalidate your existing code effective immediately.
                        The new code will expire one year from today.`) }), _jsx(Typography, { variant: "body1", children: $t('Are you sure you want to regenerate the receipt external access code for your company?') })] })),
            confirmText: $t('Regenerate Code'),
            onConfirm: doGenerateReceiptAuthCode,
            color: 'warning'
        });
    };
    useEffect(() => {
        const timer = setTimeout(() => {
            // Evaluate the Email list values
            const emails = failedLogin.value.split(';');
            const badEmails = [];
            emails.forEach((email) => {
                if (email.trim().length > 0) {
                    if (validEmailListRegex.test(email.toUpperCase()) === false) {
                        badEmails.push(email);
                    }
                }
            });
            setFailedLogin((prevState) => ({
                ...prevState,
                isValid: !(badEmails.length > 0),
                helperText: badEmails.length > 0 ? $t('Please enter valid email adddresses.') : ''
            }));
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [failedLogin.value]);
    /** Form Change Check */
    useEffect(() => {
        let changesFound = false;
        if (defaultState.minPasswordRating !== minPasswordRating) {
            changesFound = true;
        }
        if (defaultState.mfaCompanyOptOut !== mfaCompanyOptOut) {
            changesFound = true;
        }
        if (defaultState.failedLogin !== failedLogin.value) {
            changesFound = true;
        }
        setUnsavedChanges(changesFound);
    }, [defaultState, minPasswordRating, mfaCompanyOptOut, failedLogin.value]);
    /** onChange Handlers */
    const handleSaveClick = () => {
        let didSave = true;
        if (unsavedChanges) {
            if (defaultState.minPasswordRating !== minPasswordRating) {
                if (minPasswordRating) {
                    saveCompanyPreference('MinimumPasswordRating', '', minPasswordRating).then((resp) => {
                        if (resp.Status === 'success') {
                            setDefaultState((prevState) => ({
                                ...prevState,
                                minPasswordRating
                            }));
                        }
                        else {
                            didSave = false;
                        }
                    });
                }
                else {
                    deleteCompanyPreference('MinimumPasswordRating', '').then((resp) => {
                        if (resp.Status === 'success') {
                            setDefaultState((prevState) => ({
                                ...prevState,
                                minPasswordRating
                            }));
                        }
                        else {
                            didSave = false;
                        }
                    });
                }
            }
            if (!MandatoryMFA && defaultState.mfaCompanyOptOut !== mfaCompanyOptOut) {
                if (mfaCompanyOptOut) {
                    saveCompanyPreference('MFACompanyOptOut', '', '1').then((resp) => {
                        if (resp.Status === 'success') {
                            setDefaultState((prevState) => ({
                                ...prevState,
                                mfaCompanyOptOut
                            }));
                        }
                        else {
                            didSave = false;
                        }
                    });
                }
                else {
                    deleteCompanyPreference('MFACompanyOptOut', '').then((resp) => {
                        if (resp.Status === 'success') {
                            setDefaultState((prevState) => ({
                                ...prevState,
                                mfaCompanyOptOut
                            }));
                        }
                        else {
                            didSave = false;
                        }
                    });
                }
            }
            if (defaultState.failedLogin !== failedLogin.value) {
                if (failedLogin.value.length > 0) {
                    saveCompanyPreference('FailedLoginNotification', '', failedLogin.value).then((resp) => {
                        if (resp.Status === 'success') {
                            setDefaultState((prevState) => ({
                                ...prevState,
                                failedLogin: failedLogin.value
                            }));
                        }
                        else {
                            didSave = false;
                        }
                    });
                }
                else {
                    deleteCompanyPreference('FailedLoginNotification', '').then((resp) => {
                        if (resp.Status === 'success') {
                            setDefaultState((prevState) => ({
                                ...prevState,
                                failedLogin: failedLogin.value
                            }));
                        }
                        else {
                            didSave = false;
                        }
                    });
                }
            }
            // Alert on Success/Failure
            enqueueSnackbar(didSave ? $t('Saved settings!') : $t('Unable to save settings at this time.'), { variant: 'alert', AlertProps: { severity: didSave ? 'success' : 'error' } });
        }
    };
    const handleResetClick = () => {
        setMinPasswordRating(defaultState.minPasswordRating);
        setFailedLogin({
            value: defaultState.failedLogin,
            isValid: true,
            helperText: ''
        });
        setMFACompanyOptOut(defaultState.mfaCompanyOptOut);
    };
    const handleEmailListChange = (event) => {
        setFailedLogin((prevState) => ({
            // Input manipulation to enforce pattern
            ...prevState,
            value: event.target.value
                .replace(' ', ';')
                .replace(',', ';')
                .replace(/(?:\r\n|\r|\n)/g, ';')
                .replace(/;;+/, ';')
        }));
    };
    const handleMinPasswordRatingChange = (event) => {
        setMinPasswordRating(event.target.value);
    };
    const handleMFACompanyOptOutChange = (event) => {
        setMFACompanyOptOut(!event.target.checked);
    };
    return (_jsxs(SingleColumnLayout, { children: [!IsAdmin && (_jsx(Typography, { children: $t('You do not have permission to view this page. Only users with administrative permissions may access this page.') })), IsAdmin && (_jsxs(Stack, { spacing: 4, alignItems: "flex-start", children: [_jsxs(Stack, { spacing: 1, alignItems: "flex-start", children: [_jsx(Typography, { variant: "h4", children: $t('Security Settings') }), _jsxs(Typography, { variant: "body1", children: [$t('You can configure the following security settings for all users in your company.'), "\u00A0 \u00A0", _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/360046794453-Managing-Your-Company-s-Security-Settings", target: "_blank", variant: "body1", children: $t('Learn More') })] })] }), _jsx(Box, { children: _jsxs(Stack, { spacing: 2, alignItems: "flex-start", children: [_jsx(Typography, { variant: "subtitle1", children: $t('Password Requirements') }), _jsxs(Stack, { spacing: 0.5, alignItems: "flex-start", children: [_jsxs(Stack, { direction: "row", children: [_jsx(Typography, { variant: "body1", children: $t('Minimum Password Rating') }), _jsx(IconPopover, { title: $t('More information'), content: passwordInfo, iconUrl: SupportIcon, edge: false })] }), _jsxs(Select, { disabled: true, id: "minPassReq", label: "", value: minPasswordRating, onChange: handleMinPasswordRatingChange, children: [_jsx(MenuItem, { value: "2", children: $t('Better') }), _jsx(MenuItem, { value: "3", children: $t('Strong') }), _jsx(MenuItem, { value: "4", children: $t('Stronger') }), _jsx(MenuItem, { value: "5", children: $t('Strongest') })] })] }), _jsxs(Stack, { spacing: 0.5, alignItems: "flex-start", children: [_jsxs(Stack, { direction: "row", children: [_jsx(Typography, { variant: "body1", children: $t('Failed Login Notification') }), _jsx(IconPopover, { title: $t('More information'), content: failedLoginInfo, iconUrl: SupportIcon, edge: false })] }), _jsx(TextField, { value: failedLogin.value, id: "EmailNotificationList", multiline: true, rows: 5, onChange: handleEmailListChange, style: {
                                                width: '600px'
                                            }, error: !failedLogin.isValid, helperText: failedLogin.helperText })] })] }) }), _jsx(Box, { children: _jsxs(Stack, { spacing: 2, alignItems: "flex-start", children: [_jsx(Typography, { variant: "subtitle1", children: "Account Security" }), _jsxs(Stack, { direction: "row", children: [_jsx(Tooltip, { title: MandatoryMFA
                                                ? $t('Multifactor authentication is required for all users in the European Union.')
                                                : '', children: _jsx(FormControlLabel, { disabled: MandatoryMFA, checked: MandatoryMFA ? true : !mfaCompanyOptOut, style: {
                                                    marginRight: '0px'
                                                }, control: _jsx(Checkbox, { color: "primary", onChange: handleMFACompanyOptOutChange }), label: _jsx(Typography, { variant: "body1", children: $t('Require Multi-Factor Authentication for all users') }) }) }), _jsx(IconPopover, { title: $t('More information'), content: mfaInfo, iconUrl: SupportIcon, edge: false })] })] }) }), _jsx(Box, { children: _jsxs(Stack, { spacing: 2, alignItems: "flex-start", children: [_jsxs(Stack, { spacing: 1, direction: "row", children: [_jsx(Typography, { variant: "subtitle1", children: $t('Receipt External Access Code') }), _jsx(IconPopover, { title: $t('More information'), content: receiptExternalAccessCodeInfo, iconUrl: SupportIcon, edge: false })] }), _jsx(Box, { children: _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "flex-start", style: { marginLeft: '-12px' }, children: [_jsx(CopyableTextField, { id: "ReceiptAuthCode", value: receiptAuthCode.Code || 'Not set', type: "text", readOnly: true, helperText: receiptAuthCode.Code &&
                                                    `Expires: ${formatDate(receiptAuthCode.Expiration)}` }), _jsx(Button, { variant: "text", color: "primary", onClick: () => {
                                                    handleRegenerateReceiptAuthCode();
                                                }, children: receiptAuthCode.Code
                                                    ? $t('Regenerate Code')
                                                    : $t('Generate Code') })] }) })] }) }), _jsx(Spacer, { size: 16 }), _jsxs(ButtonContainer, { children: [_jsx(Button, { size: "large", variant: "outlined", onClick: handleResetClick, disabled: !unsavedChanges, children: $t('Reset') }), _jsx(Button, { size: "large", onClick: handleSaveClick, disabled: !unsavedChanges || !failedLogin.isValid, children: $t('Save') })] })] }))] }));
}
export default CorpSecuritySettings;
